/**
 * Device fingerprinting utility for trust scoring
 * Collects non-PII device characteristics to identify unique devices
 */

export interface FingerprintData {
  fingerprintHash: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  cookiesEnabled: boolean;
}

/**
 * Generate a simple hash from a string
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

/**
 * Collect device fingerprint data
 */
export function collectFingerprint(): FingerprintData {
  const screenResolution = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  const language = navigator.language || 'unknown';
  const platform = navigator.platform || 'unknown';
  const cookiesEnabled = navigator.cookieEnabled;
  
  // Collect additional characteristics for fingerprint
  const characteristics = [
    screenResolution,
    timezone,
    language,
    platform,
    navigator.userAgent,
    cookiesEnabled.toString(),
    navigator.hardwareConcurrency?.toString() || '',
    navigator.maxTouchPoints?.toString() || '',
    Intl.DateTimeFormat().resolvedOptions().locale || '',
    new Date().getTimezoneOffset().toString(),
  ];
  
  // Generate fingerprint hash
  const fingerprintString = characteristics.join('|');
  const fingerprintHash = simpleHash(fingerprintString);
  
  return {
    fingerprintHash,
    screenResolution,
    timezone,
    language,
    platform,
    cookiesEnabled,
  };
}

/**
 * Send fingerprint to the API for trust scoring
 */
export async function reportFingerprint(apiUrl: string, accessToken: string): Promise<{
  recorded: boolean;
  isNewDevice: boolean;
  isNewIp: boolean;
} | null> {
  try {
    const fingerprint = collectFingerprint();
    
    const response = await fetch(`${apiUrl}/api/auth/fingerprint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Fingerprint': fingerprint.fingerprintHash,
      },
      body: JSON.stringify(fingerprint),
    });
    
    if (!response.ok) {
      console.warn('Failed to report fingerprint:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.warn('Error reporting fingerprint:', error);
    return null;
  }
}

/**
 * Get the fingerprint hash for login requests
 */
export function getFingerprintHash(): string {
  const fingerprint = collectFingerprint();
  return fingerprint.fingerprintHash;
}

/**
 * Store fingerprint hash in localStorage for consistency
 */
export function getOrCreateFingerprintHash(): string {
  const stored = localStorage.getItem('device_fingerprint');
  if (stored) {
    return stored;
  }
  
  const hash = getFingerprintHash();
  localStorage.setItem('device_fingerprint', hash);
  return hash;
}
